/**
 * One option of a distribution, on **one line**: label, track, then the count and the share.
 *
 * The label used to sit above its own track, which doubled the height of every row and turned a
 * card with eleven buckets into a tower. On one line the numbers of every row land in the same
 * column, so they can be compared by eye without reading them.
 *
 * The percentage is always **of the rows counted** — the caller's `total` — never of a user base:
 * the BFF has no census of a tenant's users, and a denominator nobody declared is a number that
 * looks like information and is not.
 *
 * **The label is content, not code.** It is written by a person ("Monitoramento & Qualidade",
 * "Ótimo", "Prefiro o menu antigo"), so it is set in the UI face. Monospace is reserved for
 * identifiers — a question `key`, an option `value` — which are the only strings here that are
 * typed rather than read.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';

const rowStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 8fr) minmax(64px, 11fr) 92px;
  align-items: center;
  gap: var(--space-s);
  min-height: 24px;
`;

const labelStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text);
  overflow-wrap: anywhere;
  text-wrap: pretty;
`;

const trackStyle = css`
  position: relative;
  height: 10px;
  border-radius: var(--radius-round);
  background: var(--color-surface-sunken);
  overflow: hidden;
`;

const fillStyle = css`
  display: block;
  height: 100%;
  border-radius: var(--radius-round);
`;

/**
 * A bucket nobody picked, said out loud.
 *
 * An empty rail next to ten filled ones reads as a broken card, not as a zero — so the rail
 * carries the words instead of leaving the reader to infer them.
 */
const emptyTrackStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-round);
  background: transparent;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
`;

/** Count and share, apart: "0 0%" run together cannot be told apart at a glance. */
const numbersStyle = css`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  font-variant-numeric: tabular-nums;
`;

const valueStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const percentStyle = css`
  min-width: 34px;
  text-align: right;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface DistributionBarProps {
  label: string;
  value: number;
  /** Denominator. Zero renders an empty bar rather than dividing by zero. */
  total: number;
  color: string;
  /** What an empty rail should say. */
  emptyText?: string;
}

export const DistributionBar = ({
  label,
  value,
  total,
  color,
  emptyText = 'ninguém marcou',
}: DistributionBarProps): JSX.Element => {
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className={rowStyle}>
      <span className={labelStyle}>{label}</span>

      {value === 0 ? (
        <span className={emptyTrackStyle}>{emptyText}</span>
      ) : (
        <span className={trackStyle}>
          <span
            className={fillStyle}
            style={{ width: `${Math.max(percent, 2)}%`, background: color }}
          />
        </span>
      )}

      <span className={numbersStyle}>
        <span className={valueStyle}>{value.toLocaleString('pt-BR')}</span>
        <span className={percentStyle}>{percent}%</span>
      </span>
    </div>
  );
};
